import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { t } from 'i18next';

import { LeafRowCellRenderer, ParentRowCellRenderer, StatusCell } from 'core/elements';
import { AgGridFilterOption } from 'core/enums';
import { NumberHelper } from 'core/helpers';
import { formatDate, getLabel, isPromotedSingleChild } from 'core/utils';
import { ICorporatePart } from 'corporate/types';
import { statusColorMap } from 'pricing/constants';

import { ActionsCell } from './cells';
import { IFlatPriceRow } from './utils';

interface IColumnDefParams {
  corporatePart: Partial<ICorporatePart>;
  canManagePartPrices: boolean;
}

type FlatField = keyof IFlatPriceRow;

const parentFieldGetter =
  (field: FlatField) =>
  ({ data, node }: ValueGetterParams<IFlatPriceRow>) => {
    if (node?.group) {
      return node.allLeafChildren?.[0]?.data?.[field] ?? null;
    }
    if (isPromotedSingleChild({ node })) {
      return data?.[field] ?? null;
    }

    return null;
  };

const leafFieldGetter =
  (field: FlatField) =>
  ({ data, node }: ValueGetterParams<IFlatPriceRow>) => {
    if (node?.group) {
      return null;
    }

    return data?.[field] ?? null;
  };

const leafFilterValueGetter =
  (field: FlatField) =>
  ({ data }: ValueGetterParams<IFlatPriceRow>) =>
    data?.[field] ?? null;

const userValueGetter = ({ data, node }: ValueGetterParams<IFlatPriceRow>) => {
  const target = node?.group ? node.allLeafChildren?.[0]?.data : data;

  if (!target) {
    return null;
  }
  if (!node?.group && !isPromotedSingleChild({ node })) {
    return null;
  }

  return [target.userFirstName, target.userLastName].filter(Boolean).join(' ');
};

const userFilterValueGetter = ({ data }: ValueGetterParams<IFlatPriceRow>) => {
  if (!data) {
    return null;
  }

  return [data.userFirstName, data.userLastName].filter(Boolean).join(' ');
};

const formatPercentage = (value: unknown): string => {
  if (value == null || value === '') {
    return '';
  }
  const parsed = parseFloat(value as string);

  return Number.isFinite(parsed) ? `${parsed.toFixed(2)}%` : '';
};

export const columnDef = ({ corporatePart, canManagePartPrices }: IColumnDefParams): ColDef[] => {
  const baseColumns: ColDef[] = [
    {
      colId: 'status',
      headerName: t('headerStatus'),
      maxWidth: 150,
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: (params) => getLabel(params.value),
      },
      valueGetter: parentFieldGetter('status'),
      filterValueGetter: leafFilterValueGetter('status'),
      cellRenderer: StatusCell,
      cellRendererParams: { colorMap: statusColorMap },
    },
    {
      colId: 'minQuantity',
      headerName: t('headerMinQty'),
      maxWidth: 150,
      sortable: false,
      enableRowGroup: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
      },
      cellRenderer: LeafRowCellRenderer,
      valueGetter: leafFieldGetter('minQuantity'),
      valueFormatter: ({ data, value }) =>
        value != null && value !== ''
          ? NumberHelper.formatNumberPerUom({ amount: Number(value), uom: data?.uom })
          : '',
    },
    {
      colId: 'maxQuantity',
      headerName: t('headerMaxQty'),
      maxWidth: 150,
      sortable: false,
      enableRowGroup: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
      },
      cellRenderer: LeafRowCellRenderer,
      valueGetter: leafFieldGetter('maxQuantity'),
      valueFormatter: ({ data, value }) =>
        value != null && value !== ''
          ? NumberHelper.formatNumberPerUom({ amount: Number(value), uom: data?.uom })
          : '',
    },
    {
      colId: 'marginReduction',
      headerName: t('headerMarginReduction'),
      sortable: false,
      enableRowGroup: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
      },
      cellRenderer: LeafRowCellRenderer,
      valueGetter: leafFieldGetter('marginReduction'),
      valueFormatter: ({ value }) => formatPercentage(value),
    },
    {
      colId: 'discountIncrease',
      headerName: t('headerDiscountIncrease'),
      sortable: false,
      enableRowGroup: false,
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
      },
      cellRenderer: LeafRowCellRenderer,
      valueGetter: leafFieldGetter('discountIncrease'),
      valueFormatter: ({ value }) => formatPercentage(value),
    },
    {
      colId: 'startDate',
      headerName: t('headerStartDate'),
      filter: 'agDateColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN,
      },
      cellRenderer: ParentRowCellRenderer,
      valueGetter: parentFieldGetter('startDate'),
      filterValueGetter: leafFilterValueGetter('startDate'),
      valueFormatter: ({ value }) => value && formatDate(value),
    },
    {
      colId: 'endDate',
      headerName: t('headerEndDate'),
      filter: 'agDateColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN,
      },
      cellRenderer: ParentRowCellRenderer,
      valueGetter: parentFieldGetter('endDate'),
      filterValueGetter: leafFilterValueGetter('endDate'),
      valueFormatter: ({ value }) => value && formatDate(value),
    },
    {
      colId: 'updatedBy',
      headerName: t('headerUpdatedBy'),
      filter: 'agTextColumnFilter',
      cellRenderer: ParentRowCellRenderer,
      valueGetter: userValueGetter,
      filterValueGetter: userFilterValueGetter,
    },
    {
      colId: 'createdAt',
      headerName: t('headerUpdatedOn'),
      filter: 'agDateColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN,
      },
      cellRenderer: ParentRowCellRenderer,
      valueGetter: parentFieldGetter('createdAt'),
      filterValueGetter: leafFilterValueGetter('createdAt'),
      valueFormatter: ({ value }) => value && formatDate(value),
    },
  ];

  if (!canManagePartPrices) {
    return baseColumns;
  }

  return [
    {
      colId: 'actions',
      headerName: '',
      field: 'actions',
      width: 80,
      maxWidth: 80,
      resizable: false,
      sortable: false,
      filter: false,
      enableRowGroup: false,
      suppressHeaderMenuButton: true,
      suppressColumnsToolPanel: true,
      pinned: 'left',
      lockPinned: true,
      suppressMovable: true,
      cellRenderer: ActionsCell,
      cellRendererParams: { corporatePart },
    },
    ...baseColumns,
  ];
};
